import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { checkPhone, sendOtp } from "../../services/api/authApi";
import { extractErrorMessage } from "../../services/api/errorHandler";
import AuthLayout from "./components/AuthLayout";
import {
  AlertIcon,
  PhoneIcon,
  SpinnerIcon,
} from "./components/AuthIcons";
import { isValidIranianPhone, toEnglishDigits } from "./components/loginUtils";

function LoginNumber() {
  const navigate = useNavigate();
  const location = useLocation();
  // Preserve the number when returning from the OTP screen ("edit number").
  const [phone, setPhone] = useState(location.state?.phone || "");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handlePhoneChange = (e) => {
    // Accept Persian/Arabic digits, keep only numerals (max 11).
    const normalized = toEnglishDigits(e.target.value).replace(/\D/g, "").slice(0, 11);
    setPhone(normalized);
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const normalizedPhone = toEnglishDigits(phone).trim();

    if (!normalizedPhone) {
      setError("شماره موبایل را وارد کنید");
      return;
    }

    if (!isValidIranianPhone(normalizedPhone)) {
      setError("فرمت شماره موبایل نادرست است (مثال: 09123456789)");
      return;
    }

    setLoading(true);
    try {
      let status = null;
      try {
        status = await checkPhone(normalizedPhone);
      } catch (checkError) {
        // Older API versions do not expose a phone-check endpoint. Start at
        // the password screen so existing password users can log in, while
        // new users can choose the OTP path there.
        if (checkError?.response?.status === 404) {
          navigate("/LoginPassword", { state: { phone: normalizedPhone, checkUnavailable: true } });
          return;
        }
        throw checkError;
      }
      const account = status?.data?.user || status?.data;
      const hasPassword = account?.hasPassword === true
        || account?.passwordSet === true
        || account?.has_password === true;
      if (status?.success && account && hasPassword) {
        navigate("/LoginPassword", { state: { phone: normalizedPhone } });
        return;
      }

      const response = await sendOtp(normalizedPhone);
      if (response.success) {
        navigate("/LoginCode", { state: { phone: normalizedPhone, setupPassword: true } });
      } else {
        setError(extractErrorMessage({ response: { data: response } }, "خطا در ارسال کد"));
      }
    } catch (err) {
      setError(extractErrorMessage(err, "خطای شبکه. لطفاً دوباره تلاش کنید"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      badgeIcon={<PhoneIcon size={26} />}
      title="ورود و ثبت‌نام"
      subtitle="برای ورود یا ثبت‌نام، شماره موبایل خود را وارد کنید"
      footer="با ورود به این برنامه، شما تمام قوانین و مقررات را می‌پذیرید."
    >
      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <div className="auth-field">
          <label className="auth-label" htmlFor="login-phone">
            <PhoneIcon size={17} />
            شماره موبایل
          </label>
          <div className="auth-input-wrap">
            <input
              id="login-phone"
              className={`auth-input auth-input-ltr${error ? " auth-input-error" : ""}`}
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              dir="ltr"
              placeholder="09123456789"
              value={phone}
              onChange={handlePhoneChange}
              disabled={loading}
              maxLength={11}
              autoFocus
            />
          </div>
          <p className="auth-hint">کد تایید ۶ رقمی به همین شماره پیامک می‌شود</p>
        </div>

        {error && (
          <p className="auth-alert auth-alert-error" role="alert">
            <AlertIcon size={18} />
            <span>{error}</span>
          </p>
        )}

        <button className="auth-btn auth-btn-primary" type="submit" disabled={loading || !phone}>
          {loading && <SpinnerIcon />}
          {loading ? "در حال ارسال کد..." : "دریافت کد تایید"}
        </button>
      </form>
    </AuthLayout>
  );
}

export default LoginNumber;
